// src/utils/resizeImageFile.js

/**
 * Redimensiona una imagen (File/Blob) a un tamaño máximo dado,
 * manteniendo la proporción y devolviendo un nuevo File listo para subir.
 *
 * @param {File} file - Archivo original (por ejemplo, de PhotoInput)
 * @param {number} maxWidth - Ancho máximo en píxeles
 * @param {number} maxHeight - Alto máximo en píxeles
 * @param {string} [mimeType="image/jpeg"] - Tipo MIME de salida
 * @param {number} [quality=0.8] - Calidad (0–1) para JPEG/WebP
 *
 * @returns {Promise<File>} - Nueva imagen redimensionada
 */
export function resizeImageFile(
    file,
    maxWidth,
    maxHeight,
    mimeType = "image/jpeg",
    quality = 0.8
) {
    return new Promise((resolve, reject) => {
        if (!file || !(file instanceof Blob)) {
            return reject(new Error("Invalid file"));
        }

        const reader = new FileReader();

        reader.onerror = () => {
            reject(new Error("Could not read file"));
        };

        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const { width, height } = img;

                if (!width || !height) {
                    return reject(new Error("Invalid image dimensions"));
                }

                // Mantener proporción y ajustar a maxWidth / maxHeight
                const widthRatio = maxWidth / width;
                const heightRatio = maxHeight / height;
                const ratio = Math.min(widthRatio, heightRatio, 1); // nunca escalar hacia arriba

                const targetWidth = Math.round(width * ratio);
                const targetHeight = Math.round(height * ratio);

                const canvas = document.createElement("canvas");
                canvas.width = targetWidth;
                canvas.height = targetHeight;

                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    return reject(new Error("Could not get canvas context"));
                }

                // Limpiar y dibujar redimensionado
                ctx.clearRect(0, 0, targetWidth, targetHeight);
                ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            return reject(new Error("Could not generate image blob"));
                        }

                        const ext = mimeType === "image/png" ? "png" : "jpg";
                        const resizedFile = new File(
                            [blob],
                            // mismo nombre base + sufijo
                            (file.name || `image.${ext}`).replace(/\.[^.]+$/, `-resized.${ext}`),
                            { type: mimeType }
                        );

                        resolve(resizedFile);
                    },
                    mimeType,
                    quality
                );
            };

            img.onerror = () => {
                reject(new Error("Could not load image"));
            };

            img.src = reader.result;
        };

        reader.readAsDataURL(file);
    });
}